import { useCallback, useEffect, useState } from 'react'

/**
 * Traer datos y sobrevivir a que no lleguen.
 *
 * Las cuatro pantallas hacen lo mismo —pedir, mostrar mientras tanto, mostrar
 * el error si falla, reintentar— y escrito cuatro veces terminaba distinto cada
 * vez. Acá vive una sola vez, con el detalle que se olvida siempre: si el
 * jugador cambia de pantalla mientras la respuesta viaja, la respuesta vieja
 * llega igual y no tiene que pisar la nueva.
 *
 * `pedir` tiene que venir envuelto en `useCallback` con sus dependencias: es lo
 * que decide cuándo se vuelve a pedir.
 *
 * Hay dos formas de volver a pedir. `recargar` es la de la persona (el botón
 * «Actualizar», «Reintentar»): si falla, lo dice. `refrescar` es la de la
 * pantalla, cuando llega un aviso del socket o toca el refresco de respaldo: si
 * falla, se queda callada y deja a la vista lo último que sí llegó. Un wifi que
 * parpadea en medio de la ronda no puede cambiar la mesa por un cartel de error
 * que nadie pidió.
 */
export function useCarga<T>(pedir: () => Promise<T>): {
  datos: T | null
  error: string | null
  cargando: boolean
  recargar: () => void
  refrescar: () => void
} {
  const [datos, setDatos] = useState<T | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [cargando, setCargando] = useState(true)
  const [intento, setIntento] = useState({ numero: 0, callado: false })

  const recargar = useCallback(() => setIntento((i) => ({ numero: i.numero + 1, callado: false })), [])
  const refrescar = useCallback(() => setIntento((i) => ({ numero: i.numero + 1, callado: true })), [])

  useEffect(() => {
    let vigente = true
    setCargando(true)

    pedir()
      .then((resultado) => {
        if (!vigente) return
        setDatos(resultado)
        setError(null)
      })
      .catch((e: unknown) => {
        if (!vigente) return
        if (intento.callado) return
        setError(e instanceof Error ? e.message : String(e))
      })
      .finally(() => {
        if (vigente) setCargando(false)
      })

    return () => {
      vigente = false
    }
  }, [pedir, intento])

  return { datos, error, cargando, recargar, refrescar }
}
